import './application-content.scss';

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AnswerType,
  NavigationItem,
  Question,
  QuestionType,
  RaterEndorsementDetails,
  SaveAnswerInput,
  SubmissionDetailApplicationData,
  TargetAreaType,
  UpsertAnswersMutation,
  useUpsertAnswersMutation,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import {
  CircularLoader,
  ConvergeSectionTitle,
  NextButton,
  PreviousButton,
} from '../../presentation';
import {
  EditButton,
  ISubmissionProps,
  ScoreRateButton,
  SharedFooterButtons,
  isHoldStatus,
  createScoreRateWithValidation,
} from '../underwriting/overview/underwriting.submission.detail.overview';
import { FetchResult, isApolloError } from '@apollo/client';
import {
  FieldValues,
  FormProvider,
  useForm,
  useFormState,
} from 'react-hook-form';
import { Paper, Stack } from '@mui/material';
import {
  capitalizeWordsExceptPreposition,
  convertStringToTriStateBoolean,
  handleError,
} from '@converge/utils';
import { useEffect, useMemo } from 'react';

import { ApplicationQuestion } from './question';
import debounce from 'lodash.debounce';
import { useConvergeSnackBar, useConvergeNavigation } from '../../contexts';
import { useOutletContext, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUnderwritingSubmissionStateContext } from '../underwriting/underwriting.submission.context';

type NestedQuestion = Question & {
  children?: NestedQuestion[];
};

interface SectionQuestion {
  subSectionUuid: string;
  subSection?: string | null;
  questions: Question[];
}

interface IApplicationContentProps {
  selectedAppSection: NavigationItem | null;
  application: SubmissionDetailApplicationData;
  onNextClick(): void;
  onPreviousClick(): void;
  position: string;
  submissionUuid: string;
  usePrompt(message: string, enabled: boolean): void;
  questionsLoading?: boolean;
  nestedQuestions: NestedQuestion[];
}

export const ApplicationContent = ({
  selectedAppSection,
  application,
  onNextClick,
  onPreviousClick,
  position,
  submissionUuid,
  usePrompt,
  questionsLoading,
  nestedQuestions,
}: IApplicationContentProps) => {
  const { t } = useTranslation();
  const { notify } = useConvergeSnackBar();
  const navigate = useConvergeNavigation();
  const location = useLocation();
  const { isReadOnly } = useUnderwritingSubmissionStateContext();
  const { submission: submissionData } = useOutletContext<ISubmissionProps>();

  const defaultFormValues = useMemo(
    () => getFormValuesFromAnswers(nestedQuestions),
    [nestedQuestions]
  );

  const currentSectionQuestions = useMemo(
    () =>
      getSelectedApplicationSectionQuestions(
        nestedQuestions,
        selectedAppSection
      ),
    [selectedAppSection, nestedQuestions]
  );

  const methods = useForm({
    defaultValues: defaultFormValues,
  });

  const { isDirty, isSubmitted } = useFormState({
    control: methods.control,
  });

  usePrompt(
    t(
      `navigation.unsavedApplicationChanges.confirmNavigation`,
      'Any unsaved changes will be lost if you navigate away from this page. Are you sure want to leave this page ?'
    ),
    isDirty && !isSubmitted
  );

  const { upsertAnswers, saveAnswersIsLoading } = useUpsertAnswersMutation();
  const { upsertSubmission, loading: upsertSubmissionIsLoading } =
    useUpsertSubmissionMutation();

  // Create the score & rate validation function
  const scoreRateWithValidation = useMemo(() => {
    if (!submissionData) return null;
    return createScoreRateWithValidation(
      submissionData,
      navigate,
      notify,
      upsertSubmission,
      location
    );
  }, [submissionData, navigate, notify, upsertSubmission, location]);

  const onSubmitHandler = async (values: FieldValues) => {
    await upsertAnswers({
      variables: {
        data: {
          // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
          targetArea: TargetAreaType.Application,
          targetUuid: application?.submissionApplication.node?.uuid ?? '',
          answers: getAnswersFromFormValuesNested(nestedQuestions, values),
        },
      },
    });
  };

  const onCompleteApplicationEvent = async (values: FieldValues) => {
    // Use the shared validation function
    if (scoreRateWithValidation) {
      await scoreRateWithValidation(submissionUuid);
    }
  };

  const onInternalNextClick = async (values: FieldValues) => {
    if (isDirty && !isReadOnly) {
      await onSubmitHandler(values);
    }
    onNextClick();
  };

  const onInternalPreviousClick = async (values: FieldValues) => {
    if (isDirty && !isReadOnly) {
      await onSubmitHandler(values);
    }
    onPreviousClick();
  };

  const handleUpsertAnswerError = (
    response: FetchResult<UpsertAnswersMutation>
  ) => {
    if (response.errors?.length) {
      throw new Error(response.errors?.[0]?.message);
    }
  };

  const handleDebouncedSaveAnswers = useMemo(
    () =>
      debounce(async (values: FieldValues) => {
        try {
          const response = await upsertAnswers({
            variables: {
              data: {
                targetArea: TargetAreaType.Application,
                targetUuid: application?.submissionApplication.node?.uuid ?? '',
                answers: getAnswersFromFormValuesNested(
                  nestedQuestions,
                  values
                ),
              },
            },
          });
          handleUpsertAnswerError(response);
          methods.reset(methods.getValues(), {
            keepValues: true,
            keepDirty: false,
          });
        } catch (cause) {
          notify({
            message: `${
              cause instanceof Error && isApolloError(cause)
                ? cause.message
                : cause
            }`,
            variant: 'error',
          });
        }
      }, 1000),
    [upsertAnswers, application, notify, methods, nestedQuestions]
  );

  useEffect(() => {
    const sendAnswersIfDirtyForm = () => {
      const { dirtyFields } = methods.formState;
      const dirtyValues = Object.keys(dirtyFields).reduce((acc, name) => {
        acc[name] = methods.getValues(name);
        return acc;
      }, {} as FieldValues);
      if (Object.keys(dirtyValues).length > 0) {
        handleDebouncedSaveAnswers(dirtyValues);
      }
    };

    sendAnswersIfDirtyForm();
    const subscription = methods.watch(() => {
      sendAnswersIfDirtyForm();
    });
    return () => subscription.unsubscribe();
  }, [methods, methods.formState.dirtyFields, handleDebouncedSaveAnswers]);

  return (
    <FormProvider {...methods}>
      <form>
        <Paper
          elevation={0}
          className={`application-content-wrapper ${
            questionsLoading ? 'loading-wrapper' : ''
          }`}
        >
          <ApplicationContentSubSections
            isApplicationViewOnly={isReadOnly === true}
            currentSectionQuestions={currentSectionQuestions}
            questionsLoading={questionsLoading}
          />
        </Paper>

        {(saveAnswersIsLoading || upsertSubmissionIsLoading) && (
          <CircularLoader className="circular-loader-style" />
        )}

        <Stack className="footer-wrapper">
          <ApplicationContentUnderwritingFooter
            onNextClick={methods.handleSubmit(onInternalNextClick)}
            onPreviousClick={methods.handleSubmit(onInternalPreviousClick)}
            position={position}
            saveAnswersIsLoading={saveAnswersIsLoading}
            onCompleteClick={methods.handleSubmit(onCompleteApplicationEvent)}
            completeApplicationInProgress={upsertSubmissionIsLoading}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};

interface IApplicationContentSubSectionsProps {
  currentSectionQuestions: SectionQuestion[];
  isApplicationViewOnly: boolean;
  questionsLoading?: boolean;
}

const ApplicationContentSubSections = ({
  currentSectionQuestions,
  isApplicationViewOnly,
  questionsLoading,
}: IApplicationContentSubSectionsProps) => {
  if (questionsLoading) {
    return (
      <div style={{ margin: 'auto' }}>
        <CircularLoader className="circular-loader-style" />
      </div>
    );
  }
  if (!currentSectionQuestions || currentSectionQuestions.length === 0) {
    return (
      <div className="application-content-sub-section-with-no-question">
        No questions
      </div>
    );
  }

  return (
    <Stack>
      {currentSectionQuestions.map((sectionQuestion) => (
        <Stack
          key={sectionQuestion.subSectionUuid}
          className="question-content"
        >
          <ApplicationContentSubSectionHeader
            uuid={sectionQuestion.subSectionUuid}
            name={sectionQuestion.subSection}
          />
          <ApplicationQuestion
            questions={sectionQuestion.questions}
            isApplicationViewOnly={isApplicationViewOnly}
          />
        </Stack>
      ))}
    </Stack>
  );
};

const ApplicationContentSubSectionHeader = ({
  name,
  uuid,
}: {
  uuid: string;
  name?: string | null;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {name && (
        <ConvergeSectionTitle showSeparator>
          {t(
            `application.subSections.${uuid}`,
            name && capitalizeWordsExceptPreposition(name)
          )}
        </ConvergeSectionTitle>
      )}
    </>
  );
};

interface IApplicationContentUnderwritingFooterProps {
  onNextClick(): void;
  onPreviousClick(): void;
  position: string;
  saveAnswersIsLoading: boolean;
  onCompleteClick(): void;
  completeApplicationInProgress: boolean;
}

const ApplicationContentUnderwritingFooter = ({
  onNextClick,
  onPreviousClick,
  position,
  saveAnswersIsLoading,
  onCompleteClick,
  completeApplicationInProgress,
}: IApplicationContentUnderwritingFooterProps) => {
  const {
    isEditButtonEnabled,
    isReadOnly,
    submission,
    isScoreAndRateButtonEnabled,
  } = useUnderwritingSubmissionStateContext();

  const { submission: submissionInfo } = useOutletContext<ISubmissionProps>();

  const holdStatus = isHoldStatus(submissionInfo);
  return (
    <div className="application-content-footer">
      <div className="footer-container">
        <Stack className="left-side-footer">
          {EditButton({
            isEditButtonEnabled: isEditButtonEnabled || false,
            submissionUuid: submission.uuid,
            holdStatus,
            pageType: 'Questionnaire',
          })}
          {ScoreRateButton({
            isScoreAndRateButtonEnabled: isScoreAndRateButtonEnabled || false,
            holdStatus,
            loading: saveAnswersIsLoading || completeApplicationInProgress,
            onClick: onCompleteClick,
          })}
          {SharedFooterButtons({
            submission,
            holdStatus,
          })}
        </Stack>
        <Stack flexDirection={'row'} className="next-pervious-container">
          <div>
            <PreviousButton
              onClick={onPreviousClick}
              loading={saveAnswersIsLoading || completeApplicationInProgress}
            ></PreviousButton>
          </div>
          <div>
            <NextButton
              className="application-content-next-button"
              style={{ marginLeft: '10px' }}
              onClick={onNextClick}
              loading={saveAnswersIsLoading || completeApplicationInProgress}
              disabled={position === 'last' && !isReadOnly}
            ></NextButton>
          </div>
        </Stack>
      </div>
    </div>
  );
};

const findQuestionById = (
  questions: NestedQuestion[],
  id: string
): NestedQuestion | undefined => {
  for (const question of questions) {
    if (question.uuid === id) return question;
    if (question.children?.length) {
      const found = findQuestionById(question.children, id);
      if (found) return found;
    }
  }
  return undefined;
};

export const processAnswer = (
  question: Question,
  key: string,
  value: string
) => {
  if (!question) {
    handleError({
      source: 'getAnswersFromFormValues',
      message: `Unable to find question matching UUID "${key}" at ${window.location.href}`,
      context: {
        frontendData: {
          question,
          value,
        },
      },
    });
    return false;
  }

  const saveAnswerInput: SaveAnswerInput = {
    questionUuid: question.uuid,
    type: AnswerType.String,
    answer: value,
  };

  switch (question?.type?.toUpperCase()) {
    case QuestionType.String:
      saveAnswerInput.type = AnswerType.String;
      saveAnswerInput.answer = value ?? '';
      break;
    case QuestionType.Multiline:
      saveAnswerInput.type = AnswerType.String;
      saveAnswerInput.answer = value;
      break;
    case QuestionType.List: {
      saveAnswerInput.type = AnswerType.String;
      saveAnswerInput.answer = value;
      break;
    }
    case QuestionType.Int:
    case QuestionType.Float:
    case QuestionType.Currency: {
      saveAnswerInput.type = AnswerType.Float;
      saveAnswerInput.answer = `${Number(value)}`;
      break;
    }
    case QuestionType.Bool: {
      saveAnswerInput.type = AnswerType.Bool;
      saveAnswerInput.answer = `${value === null ? 'null' : value}`;
      break;
    }
    case QuestionType.Option: {
      saveAnswerInput.type = AnswerType.Bool;
      saveAnswerInput.answer = `${value === null ? 'null' : value}`;
      break;
    }
    case QuestionType.Date:
      saveAnswerInput.type = AnswerType.Date;
      saveAnswerInput.answer = value ? new Date(value).toISOString() : '';
      break;
    default:
      return false;
  }
  return saveAnswerInput;
};
const filterInputs = (inputs: (false | SaveAnswerInput)[]) =>
  inputs.filter((inputOrFalse) => inputOrFalse !== false) as SaveAnswerInput[];

export const getAnswersFromFormValuesNested = (
  questions: NestedQuestion[],
  values: FieldValues
): SaveAnswerInput[] => {
  const inputs = Object.entries(values).map(
    ([key, value]): SaveAnswerInput | false => {
      const question = findQuestionById(questions, key);
      return question ? processAnswer(question, key, value) : false;
    }
  );
  return filterInputs(inputs);
};

export const getAnswersFromFormValues = (
  questions: RaterEndorsementDetails['questions'],
  values: FieldValues
): SaveAnswerInput[] => {
  const inputs = Object.entries(values).map(
    ([key, value]): SaveAnswerInput | false => {
      const question = questions.edges.find((e) => e.node.uuid === key)?.node;
      return question ? processAnswer(question, key, value) : false;
    }
  );
  return filterInputs(inputs);
};

const processQuestion = (
  resp: FieldValues,
  question: NestedQuestion,
  prefix?: string
) => {
  const id = prefix ? `${prefix}.${question.uuid}` : question.uuid;

  switch (question?.type?.toUpperCase()) {
    case QuestionType.String:
      resp[id] = question.answer?.answer ?? '';
      break;
    case QuestionType.Multiline:
      resp[id] = question.answer?.answer ?? '';
      break;
    case QuestionType.List: {
      resp[id] = question.answer?.answer ?? '';
      break;
    }
    case QuestionType.Int:
      resp[id] = question.answer?.answer ? Number(question.answer?.answer) : '';
      break;
    case QuestionType.Float:
      resp[id] = question.answer?.answer ? Number(question.answer?.answer) : '';
      break;
    case QuestionType.Currency: {
      resp[id] = question.answer?.answer ? Number(question.answer?.answer) : '';
      break;
    }
    case QuestionType.Bool: {
      resp[id] =
        typeof question.answer?.answer == 'string'
          ? convertStringToTriStateBoolean(question.answer?.answer)
          : question.answer?.answer;
      break;
    }
    case QuestionType.Option: {
      resp[id] = question.answer?.answer;
      break;
    }
    case QuestionType.Date:
      resp[id] = question.answer?.answer ?? '';
      break;
  }
  question.children?.forEach((child) => processQuestion(resp, child, prefix));

  return resp;
};

export const getFormValuesFromAnswers = (
  questions: NestedQuestion[],
  prefix?: string
): FieldValues => {
  return (
    questions.reduce((resp, question) => {
      return processQuestion(resp, question, prefix);
    }, {} as FieldValues) ?? {}
  );
};

export const getFormValuesFromAnswersNested = (
  questions: RaterEndorsementDetails['questions'],
  prefix?: string
): FieldValues => {
  return (
    questions.edges.reduce((resp, { node: question }) => {
      return processQuestion(resp, question, prefix);
    }, {} as FieldValues) ?? {}
  );
};

const getSelectedApplicationSectionQuestions = (
  nestedQuestions: any,
  selectedAppSection: NavigationItem | null
): any => {
  const questions =
    nestedQuestions &&
    nestedQuestions.length > 0 &&
    nestedQuestions
      .filter((e: any) => e.sectionUuid === selectedAppSection?.value)
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  if (!questions || questions.length === 0) {
    return [];
  }
  const sectionQuestions = questions.reduce((prev: any, curr: any) => {
    const existing = prev.find((s: any) => {
      return s.subSectionUuid === curr.subSectionUuid;
    });
    if (existing) {
      existing.questions.push(curr);
    } else {
      prev.push({
        subSectionUuid: curr.subSectionUuid,
        questions: [curr],
        subSection: curr.subSection,
      });
    }
    return prev;
  }, [] as unknown as SectionQuestion[]);

  return sectionQuestions;
};
